import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { SpellID } from '@/game/prefabs/Duel/SpellIcon';
import Duel from '@/game/scenes/DuelScenes/Duel';
import { EventBus } from '../EventBus';
import { AnimationUtils, Direction } from '../Utils/AnimationUtils';
import GAME_EVENTS from '../Utils/gameEventsDef';

export interface DuelistConfig {
  maxHP: number;
  currHP?: number;
  avatar: Phaser.GameObjects.Container;
  isActive: boolean;
  index: number;
  onDuelistHpModified?: () => void;
}

export default class Duelist {
  maxHP: number;
  currHP: number;
  avatar: Phaser.GameObjects.Container;
  isActive: boolean;
  index: number;
  onDuelistHpModified?: () => void;
  projectileOffset: Phaser.Math.Vector2 = new Phaser.Math.Vector2(0, -35);

  constructor(data: DuelistConfig) {
    this.maxHP = data.maxHP;
    this.currHP = data.currHP || data.maxHP;
    this.avatar = data.avatar;
    this.isActive = data.isActive;
    this.onDuelistHpModified = data.onDuelistHpModified;
    this.index = data.index;

    EventsManager.registerEventOn(
      this.avatar.scene,
      GAME_EVENTS.SET_ACTIVE,
      this.handleSetActive,
      this,
    );
  }

  handleSetActive(duelist: Duelist) {
    if (this === duelist) {
      this.isActive = true;
    } else {
      this.isActive = false;
    }
  }

  async castSpell(duelContext: Duel, spell: SpellID) {
    AnimationUtils.setAttacking(
      this.avatar,
      { repeat: 0, frameRate: 2 },
      this.index !== 0, //Flip X if we're not the player.
    ).then(() => {
      AnimationUtils.setIdle(this.avatar, Direction.DOWN);
    });

    // Create projectile.
    // Delayed spell effect.
    await waitForSeconds(duelContext.time, 0.5);
    this.performSpellHitFeedback(duelContext, spell);
    await waitForSeconds(duelContext.time, 0.5);
  }

  modifyCurrentHP(delta: number) {
    this.currHP += delta;
    if (delta < 0) {
      AnimationUtils.setHurt(
        this.avatar as Phaser.GameObjects.Container,
        { repeat: 0, duration: 1000 },
        () => {
          if (!this.isDefeated()) {
            AnimationUtils.setIdle(
              this.avatar as Phaser.GameObjects.Container,
              Direction.DOWN,
            );
          }
        },
      );
    }
    if (this.currHP <= 0) {
      this.defeated();
    }
    this.onDuelistHpModified?.();
  }

  async performSpellHitFeedback(duelContext: Duel, spell?: SpellID) {
    PhaserAudioManager.playSFX(
      duelContext,
      this.getSpellEffectsFromSpellID(spell).sfx.cast,
    );

    for (var i = 0; i < duelContext.duelists.length; i++) {
      const duelist = duelContext.duelists[i];
      const spellEffects = this.getSpellEffectsFromSpellID(spell);
      if (duelist !== this) {
        // Get the position of the avatar in the world.
        const avatarPos = this.avatar
          .getWorldTransformMatrix()
          .transformPoint(0, 0);

        const projectileObject = this.avatar.scene.add
          .sprite(
            avatarPos.x + this.projectileOffset.x,
            avatarPos.y + this.projectileOffset.y,
            spellEffects.texture,
          )
          .setScale(1)
          .setDepth(999);

        // Get the position of the target avatar in the world.
        const targetPos = duelist.avatar
          .getWorldTransformMatrix()
          .transformPoint(0, 0);
        this.avatar.scene.add.tween({
          targets: projectileObject,
          x: targetPos.x,
          y: targetPos.y,
          duration: 400,
          onComplete: () => {
            projectileObject.destroy();
          },
        });
        await waitForSeconds(duelContext.time, 0.4);
        const hitEffectPos = duelist.avatar
          .getWorldTransformMatrix()
          .transformPoint(0, 0);
        const spellObject = this.avatar.scene.add
          .sprite(
            hitEffectPos.x + this.projectileOffset.x,
            hitEffectPos.y + this.projectileOffset.y,
            spellEffects.texture,
          )
          .setDepth(99)
          .setScale(1);
        spellObject.play(spellEffects.animationKey);
        PhaserAudioManager.playSFX(
          duelContext,
          this.getSpellEffectsFromSpellID(spell).sfx.hit,
        );

        spellObject.on('animationcomplete', () => {
          spellObject.destroy();
        });
      }
    }
  }

  getSpellScaleFromWordLength(wordLength: number) {
    const baseScale = 1;
    const scalePerLetter = 0.2;
    const minLetters = 2;
    return Math.min(2, (wordLength - minLetters) * scalePerLetter + baseScale);
  }

  getSpellEffectsFromSpellID(spellID?: SpellID) {
    switch (spellID) {
      case SpellID.fireball:
        return {
          texture: 'fireblast',
          animationKey: 'fireblast',
          sfx: {
            cast: 'fireball-cast',
            hit: 'fireball-hit',
          },
        };
      case SpellID.iceBlast:
        return {
          texture: 'iceblast',
          animationKey: 'iceblast',
          sfx: {
            cast: 'iceblast-cast',
            hit: 'iceblast-hit',
          },
        };
      case SpellID.arcaneBlast:
        return {
          texture: 'arcaneblast',
          animationKey: 'arcaneblast',
          sfx: {
            cast: 'arcaneblast-cast',
            hit: 'arcaneblast-hit',
          },
        };
    }
    // Default effect if the ID cannot be found.
    return {
      texture: 'fireblast',
      animationKey: 'fireblast',
      sfx: {
        cast: 'fireball-cast',
        hit: 'fireball-hit',
      },
    };
  }

  isDefeated() {
    return this.currHP <= 0;
  }

  async defeated() {
    // This duelist was defeated.
    EventBus.emit(GAME_EVENTS.DUELIST_WAS_DEFEATED, { duelist: this });
    await waitForSeconds(this.avatar.scene.time, 1);
    // Play the enemy defeated animation.
    AnimationUtils.setDefeated(this.avatar, {});
  }
}
